import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { BsFileEarmarkTextFill } from "react-icons/bs";
import AppContext from "../../../../appContext";
import AppButton from "../../../../shared/components/generic/button/app-button";
import FormGeneric from "../../../../shared/components/generic/form/form-generic";
import InnerPageStructure from "../../../../shared/components/generic/inner-page-structure/inner-page-structure";
import PageStateMessage from "../../../../shared/components/generic/page-state-message/page-state-message";
import PageSteps from "../../../../shared/components/generic/page-steps/page-steps";
import { TokenUtils } from "../../../../shared/utils/TokenUtils";
import SimulationSummary from "../../components/simulation-summary/simulation-summary";
import { dataSubmissionFormConfig } from "../../config/simulacao/envio-dados/form-config";
import { summaryConfig } from "../../config/simulacao/summary-config";
import { simulationSteps } from "../../config/simulacao/steps-config";
import Swal from "sweetalert2";
import { useSubmitApplication } from "../../hooks/useSubmitApplication";
import {
	clearSimulationMode,
	saveSimulationMode
} from "../../utils/simulacao-utils";
import AdditionalInformation from "../../../../shared/components/generic/additional-information/additional-information";
import { showSubmitApplicationSuccessAlert } from "../../../../shared/components/generic/alert/success-alert/success-alert";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import { removeClearedValidationErrors } from "../../../utils/validation-utils";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";
import "../simulacao.css";

const EnvioDadosSimulacao = () => {
	const context = useContext(AppContext);
	const history = useHistory();
	const location = useLocation();

	const [hasModuleAccess, setHasModuleAccess] = useState(false);
	const formSectionRef = useRef(null);
	const [highlightForm, setHighlightForm] = useState(false);
	const [clearedValidationErrors, setClearedValidationErrors] = useState({});
	const [dataSubmissionActionLoadingMessage, setDataSubmissionActionLoadingMessage] = useState("");

	const getValidationErrorMessages = errors => {
		return [
			...new Set(
				Object.entries(errors || {})
					.filter(([fieldName]) => !fieldName.endsWith("InvalidItems"))
					.map(([, message]) => message)
					.filter(Boolean)
			)
		];
	};

	const getDeliveryFormElement = () => {
		return document.getElementById(envioDadosFormId);
	};

	const focusDeliveryForm = () => {
		setHighlightForm(true);

		const formElement = getDeliveryFormElement();

		if (!formElement) {
			return;
		}

		window.setTimeout(() => {
			formElement.scrollIntoView({
				behavior: "smooth",
				block: "center"
			});

			formElement.focus();
		}, 150);
	};

	const calculationData = location.state?.calculationData;
	const returnState = location.state?.returnState;
	const deliveryData = location.state?.deliveryData;

	const dadosEnvioConfig = dataSubmissionFormConfig({
		data: deliveryData
	});

	const resumoSimulacaoConfig = summaryConfig({
		simulation: calculationData
	});

	const termsAndConditions =
		calculationData?.termosCondicoes ||
		calculationData?.raw?.termosCondicoes ||
		"";

	const termsAndConditionsItems = termsAndConditions
		.split(/\r\n|\r|\n|\\r\\n|\\n|\\r/)
		.map(item => item.trim())
		.filter(Boolean);

	const innerPageElements = {
		backLabel: [{ label: "Envio de Simulação" }]
	};

	const handleBack = () => {
		const path =
			returnState?.returnPath ||
			"/pacto-simulador/simulacao";

		history.push(path, {
			restoreSimulationState: returnState,
			mode: returnState?.mode || "create"
		});
	};

	const envioDadosFormId = "envio-dados-simulacao-form";

	const {
		loading: submitApplicationLoading,
		validationErrors: submitApplicationValidationErrors,
		submitApplication
	} = useSubmitApplication();

	const simulationId =
		calculationData?.id ||
		calculationData?.simulacaoId ||
		returnState?.simulationId;

	const handleDataSubmissionFieldChange = ({ name }) => {
		setHighlightForm(false);

		setClearedValidationErrors(previous => ({
			...previous,
			[name]: true,
			...(name === "emails"
				? { emailsInvalidItems: true }
				: {})
		}));
	};

	const handleSendSimulation = async values => {
		setClearedValidationErrors({});

		if (!simulationId) {
			await showCommonAlert({
				message: "Não foi possível identificar a simulação para envio.",
				customClass: {
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		let result;

		setDataSubmissionActionLoadingMessage("Enviando simulação...");

		try {
			result = await submitApplication(simulationId, values);
		} finally {
			setDataSubmissionActionLoadingMessage("");
		}

		if (result?.validationErrors) {
			const errorMessages = getValidationErrorMessages(result.validationErrors);

			if (document.activeElement) {
				document.activeElement.blur();
			}

			await showCommonAlert({
				title: "Aviso",
				html: errorMessages.join("<br />"),
				icon: "warning",
				focusConfirm: false,
				customClass: {
					confirmButton: "alert-form-button"
				}
			});

			focusDeliveryForm();

			return;
		}

		if (!result?.ok) {
			await showCommonAlert({
				message: result?.error,
				fallbackMessage: "Não foi possível enviar a simulação.",
				customClass: {
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		await showSubmitApplicationSuccessAlert({
			title: "Simulação enviada com sucesso",
			description: "O resumo da simulação foi enviado para o e-mail do cliente e já está disponível no seu histórico.",
			actions: [
				{
					label: "Ir para o Histórico",
					endIcon: "arrowRight",
					onClick: () => {
						Swal.close();

						clearSimulationMode(simulationId);
						clearSimulationMode("create");

						history.replace("/pacto-simulador");
					}
				},
				{
					label: "Nova Simulação",
					startIcon: "plusCircle",
					variant: "secondary",
					onClick: () => {
						Swal.close();

						clearSimulationMode(simulationId);
						clearSimulationMode("create");

						saveSimulationMode({
							simulationId: "create",
							mode: "create"
						});

						history.replace("/pacto-simulador/simulacao", {
							mode: "create"
						});
					}
				}
			]
		});
	};

	useEffect(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "auto"
		});
	}, []);

	useEffect(() => {
		const formElement = getDeliveryFormElement();

		if (!formElement) {
			return;
		}

		formElement.classList.toggle(
			"envio-dados-simulacao__form--highlight",
			highlightForm
		);

		if (highlightForm) {
			formElement.setAttribute("tabindex", "-1");
		}

		return () => {
			formElement.classList.remove("envio-dados-simulacao__form--highlight");
		};
	}, [highlightForm]);

	useEffect(() => {
		setHasModuleAccess(TokenUtils.hasModuloSimulador());
	}, []);

	if (!context.userSession) {
		return (
			<PageStateMessage title="Carregando..." />
		);
	}

	if (!hasModuleAccess) {
		return (
			<PageStateMessage
				title="Acesso Negado"
				description="Você não possui acesso ao módulo Simulador."
			/>
		);
	}

	const dataSubmissionValidationErrors = removeClearedValidationErrors({
		errors: submitApplicationValidationErrors,
		clearedErrors: clearedValidationErrors
	});

	return (
		<InnerPageStructure innerPageElements={innerPageElements}>
			<PageLoading
				actionMessage={dataSubmissionActionLoadingMessage}
				states={[
					{
						loading: submitApplicationLoading,
						message: "Enviando simulação..."
					}
				]}
			/>
			<PageSteps
				steps={simulationSteps}
				currentStep={2}
				className="simulacao__steps"
			/>

			<FormGeneric
				formId={envioDadosFormId}
				formFields={dadosEnvioConfig}
				message={null}
				onSubmit={handleSendSimulation}
				errors={dataSubmissionValidationErrors}
				onFieldChange={handleDataSubmissionFieldChange}
			/>

			<div className="envio-dados-simulacao__summary-box">
				<div className="envio-dados-simulacao__summary-title">
					<BsFileEarmarkTextFill />
					<span>Resumo da Simulação</span>
				</div>

				<SimulationSummary groups={resumoSimulacaoConfig} />
			</div>

			<div className="envio-dados-simulacao__actions form-generic">
				<AppButton
					type="button"
					onClick={handleBack}
					startIcon="arrowLeft"
					className="envio-dados-simulacao__button envio-dados-simulacao__button--outline"
				>
					Refazer Simulação
				</AppButton>

				<AppButton
					type="submit"
					form={envioDadosFormId}
					disabled={submitApplicationLoading}
					endIcon="arrowRight"
					className="envio-dados-simulacao__button"
				>
					Enviar Simulação
				</AppButton>
			</div>

			<AdditionalInformation
				title="Condição de reajuste dos objetivos"
				items={termsAndConditionsItems}
			/>
		</InnerPageStructure>
	);
};

export default EnvioDadosSimulacao;